import type { TradeItem, LocalTradeFilters } from '@/types';
import type { ColumnPinSide, ColumnConfig } from '@/stores/trades/trades-column-store';
import { TRADES_FILTERABLE_COLUMN_IDS } from '@/utils/trades/filter-column-mapping';
import { formatTradeCellValue } from '@/utils/trades/cell-format';
import { getNativeAmount } from '@/utils/trades/settlement-ccy';
import { isServerOrderable } from '@/utils/trades/map-server-params';
import { useTableContextMenus } from '@/hooks/useTableContextMenus';
import type { ColumnDef } from '@tanstack/react-table';

// Trades has no per-cell selection (removed in LR-104), but the shared table
// context-menu hook still requires cell-selection inputs. Feed stable inert
// stubs: the cell context menu is never rendered on Trades — only the header
// menu (sort/filter/pin/hide) is — so these are never exercised. Module-level
// constants keep their identity stable across renders.
const EMPTY_COL_INDEX = new Map<string, number>();
const EMPTY_CELL_SET = new Set<string>();
const getEmptyCellSet = (): Set<string> => EMPTY_CELL_SET;
const getZero = (): number => 0;
const noop = (): void => {};
const alwaysFalse = (): boolean => false;

/** Map column IDs to their LocalTradeFilters array key for "Filter by value". */
const COLUMN_ID_TO_FILTER_ARRAY_KEY: Partial<Record<string, keyof LocalTradeFilters>> = {
  status_display: 'statuses',
  counterparty: 'counterparties',
  trade_type_display: 'tradeTypes',
  collateral_type: 'collateralTypes',
  agreement_display: 'agreementTypes',
  term_type_display: 'termTypes',
  currency: 'currencies',
};

/** Map display column IDs to the raw data field used in filters. */
const DISPLAY_TO_RAW_FIELD: Record<string, keyof TradeItem> = {
  status_display: 'status',
  trade_type_display: 'trade_type',
  agreement_display: 'agreement',
  term_type_display: 'term_type',
};

/** Map column IDs to their min/max filter keys for numeric "filter by value". */
const COLUMN_ID_TO_NUMERIC_FILTER_KEYS: Partial<Record<string, { min: keyof LocalTradeFilters; max: keyof LocalTradeFilters }>> = {
  quantity: { min: 'quantityMin', max: 'quantityMax' },
  contract_value: { min: 'contractValueMin', max: 'contractValueMax' },
  margin: { min: 'marginMin', max: 'marginMax' },
  gross_price: { min: 'grossPriceMin', max: 'grossPriceMax' },
  daily_earnings: { min: 'dailyEarningsMin', max: 'dailyEarningsMax' },
};

interface UseTradesContextMenusParams {
  columnConfig: ColumnConfig[];
  columns: ColumnDef<TradeItem, unknown>[];
  sorting: { id: string; desc: boolean }[];
  // LR-671: a plain setter — sorting lives in trades-data-store now, not useState.
  setSorting: (sorting: { id: string; desc: boolean }[]) => void;
  getPinState: (columnId: string) => ColumnPinSide;
  pinColumn: (columnId: string, side: ColumnPinSide) => void;
  toggleColumnVisibility: (columnId: string) => void;
  handleFilterColumnClick: (columnId: string) => void;
  localTradesFilters: LocalTradeFilters;
  setLocalTradesFilters: (filters: LocalTradeFilters) => void;
  sortedDataRef: React.MutableRefObject<TradeItem[]>;
  colIds: string[];
}

export function useTradesContextMenus({
  columnConfig,
  columns,
  sorting,
  setSorting,
  getPinState,
  pinColumn,
  toggleColumnVisibility,
  handleFilterColumnClick,
  localTradesFilters,
  setLocalTradesFilters,
  sortedDataRef,
  colIds,
}: UseTradesContextMenusParams) {
  return useTableContextMenus<TradeItem, LocalTradeFilters>({
    columnConfig,
    columns,
    sorting,
    isSortable: isServerOrderable,
    setSorting,
    getPinState,
    pinColumn,
    toggleColumnVisibility,
    handleFilterColumnClick,
    sortedDataRef,
    filterConfig: {
      filterableColumnIds: TRADES_FILTERABLE_COLUMN_IDS,
      arrayFilterMap: COLUMN_ID_TO_FILTER_ARRAY_KEY,
      numericFilterMap: COLUMN_ID_TO_NUMERIC_FILTER_KEYS,
      // LR-628: the Daily Earnings cell renders the effective figure, and the
      // sidebar filter now compares that same figure — so the value this menu
      // WRITES has to come from there too. Reading the raw field would label the
      // item "Equals 23.15" and then filter on the booked 31.50, i.e. on a number
      // that appears nowhere on screen.
      getNumericFilterValue: (columnId, item) =>
        columnId === 'daily_earnings'
          ? getNativeAmount(item, 'daily_earnings')
          : ((item as unknown as Record<string, unknown>)[columnId] as number | null),
      displayToRawField: DISPLAY_TO_RAW_FIELD,
      getCellDisplayValue: (columnId, item) => formatTradeCellValue(item, columnId),
      filters: localTradesFilters,
      setFilters: setLocalTradesFilters,
    },
    colIds,
    selectedCellsForCopy: getEmptyCellSet,
    selectedCellCount: getZero,
    selectColumn: noop,
    deselectColumn: noop,
    isColumnFullySelected: alwaysFalse,
    colIdToIndex: EMPTY_COL_INDEX,
    tableName: 'trades',
  });
}
